import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { GetOrderById, MarkOrderFavApi } from "../services/ApiService";
import {
  OrderModel,
  OrderModifierGroupModel,
  OrderProductModel,
} from "../models";
import { useAuth, useStore } from "../hooks";
import { Layout } from "../layouts";
import { formatCurrency, formatDate } from "../utilities";
import { OrderDetailModal, OrderStatus } from "../components";
import OrderLoyalty from "../components/order/OrderLoyalty";
import {
  Button,
  OverlayTrigger,
  Tooltip,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import { BiRefresh } from "react-icons/bi";
import {
  FiMapPin,
  FiFileText,
  FiPhone,
  FiHome,
  FiChevronRight,
} from "react-icons/fi";
import { RiMapPinLine } from "react-icons/ri";
import { CheckCircleFill } from "react-bootstrap-icons";
import { DoorDashIcon } from "../icons";
import { Toast } from "../components/Toast";
import { OrderConfirmationSkeleton } from "../components/skeletons/OrderConfirmationSkeleton";

const OrderConfirmationPage = () => {
  const { storeId, selectedStore } = useStore();
  const { customerId } = useAuth();
  const { orderId } = useParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [order, setOrder] = useState<OrderModel>();
  const [activeModal, setActiveModal] = useState<string>("");
  const [showBanner, setShowBanner] = useState(false);
  const handleClose = () => setActiveModal("");

  useEffect(() => {
    fetchData();
  }, [orderId]);

  async function fetchData(isUpdate = false) {
    try {
      setIsLoading(true);
      const { status, message, data } = await GetOrderById({
        store_id: storeId,
        customer_id: customerId,
        order_id: orderId,
      });
      if (status) setOrder(data);
      else {
        Toast.error(message);
      }
      if (isUpdate) Toast.info("Order Status Updated Successfully");
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleRefresh = () => {
    fetchData(true);
  };

  const handleFavOrder = async () => {
    if (!order) return;

    try {
      const { status, data, message } = await MarkOrderFavApi({
        store_id: storeId,
        customer_id: customerId,
        order_id: order.order_id,
        is_fav: !order.is_favourite,
        is_fav_name: "",
      });

      if (status) {
        setOrder({ ...order, is_favourite: data.is_favourite });

        Toast.success(
          data.is_favourite
            ? "Order added to favorite!"
            : "Order removed from favorite!",
        );
      } else {
        Toast.error(message);
      }
    } catch (error) {
      console.error("Error updating favourite status:", error);
    } finally {
      handleClose();
    }
  };

  const renderTooltip = (props: any) => (
    <Tooltip id="button-tooltip" {...props}>
      Refresh Order Status
    </Tooltip>
  );

  if (isLoading || !order) {
    return <OrderConfirmationSkeleton />;
  }

  const productQty = order.products.reduce(
    (quantity, item) => Number(item.product_qty) + quantity,
    0,
  );

  return (
    <Layout
      className="order-confirmation-layout"
      showLoader={isLoading}
      hideRightSidebarOnMobile={false}
    >
      <div className="order-confirmation-container">
        <Row>
          <Col md={12} lg={7} sm={12} xl={8}>
            {showBanner && (
              <>
                <Alert
                  variant="success"
                  dismissible
                  onClose={() => setShowBanner(false)}
                  className="d-flex gap-3 alert-success-style"
                >
                  <CheckCircleFill size={28} />
                  <div>
                    <strong>Order placed successfully</strong>
                    <br />
                    <small>
                      Your order #{order?.order_id} has been sent to{" "}
                      {selectedStore?.store_name}.
                    </small>
                  </div>
                </Alert>
              </>
            )}

            <div className="thank-you-header d-flex align-items-start justify-content-start gap-3">
              <CheckCircleFill size={45} className="mt-2" />
              <div>
                <h1>Order placed successfully</h1>
                <p>
                  Thank you, {order?.customer.name}! {selectedStore?.store_name}{" "}
                  has received your {order?.order_type} order.
                </p>
              </div>
            </div>

            <div className="confirmation-card quick-actions-card">
              {/* Order Info Only */}
              <div className="quick-action-item">
                <div className="info-icon">
                  <FiFileText size={20} />
                </div>
                <div className="quick-action-content">
                  <h4>Order</h4>
                  <p>
                    #{order?.order_id} · {order?.order_type} ·{" "}
                    {order?.create_ts ? formatDate(order?.create_ts) : ""}
                  </p>
                </div>
              </div>
              {/* End */}

              {/* address */}
              <div className="quick-action-item">
                <div className="info-icon">
                  <FiMapPin size={20} />
                </div>
                <div className="quick-action-content">
                  <h4>
                    {order?.order_type.toLowerCase() === "delivery"
                      ? "Deliver To"
                      : order?.order_type.toLowerCase() === "pickup"
                        ? "Pickup From"
                        : order?.order_type.toLowerCase() === "delivery"
                          ? "Dine In"
                          : order?.order_type.toLowerCase() === "giftcard"
                            ? "Recipient"
                            : ""}
                  </h4>
                  <p>{order?.address}</p>
                </div>
              </div>
              {/* End */}

              {/* Order details opens modal */}
              <button
                type="button"
                className="quick-action-item"
                onClick={() => setActiveModal("orderDetail")}
              >
                <div className="info-icon">
                  <FiFileText size={22} />
                </div>
                <div className="quick-action-content">
                  <h4>View Order Details</h4>
                  <p>See items, notes & total</p>
                </div>
                <FiChevronRight size={20} className="quick-action-chevron" />
              </button>
              {/* End */}

              {/* Order Status */}
              <button
                type="button"
                className="quick-action-item"
                onClick={() => {
                  document
                    .querySelector(".status-title")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <div className="info-icon">
                  <RiMapPinLine size={22} />
                </div>
                <div className="quick-action-content">
                  <h4>Track Order Status</h4>
                  <p>Real-time updates</p>
                </div>
                <FiChevronRight size={20} className="quick-action-chevron" />
              </button>
              {/* End */}

              {selectedStore?.store_phone && (
                <a
                  href={`tel:${selectedStore?.store_phone || ""}`}
                  className="quick-action-item"
                >
                  <div className="info-icon">
                    <FiPhone size={22} />
                  </div>
                  <div className="quick-action-content">
                    <h4>Call Store</h4>
                    <p>{selectedStore?.store_phone || ""}</p>
                  </div>
                  <FiChevronRight size={20} className="quick-action-chevron" />
                </a>
              )}

              {/* Back to menu */}
              <Link to="/menu" className="quick-action-item">
                <div className="info-icon">
                  <FiHome size={22} />
                </div>
                <div className="quick-action-content">
                  <h4>Back to Menu</h4>
                  <p>Continue ordering</p>
                </div>
                <FiChevronRight size={20} className="quick-action-chevron" />
              </Link>
              {/* End */}
            </div>

            {order && (
              <div className="confirmation-card">
                <div className="status-title">
                  <span>Order Status</span>
                  <OverlayTrigger placement="left" overlay={renderTooltip}>
                    <Button variant="light" size="sm" onClick={handleRefresh}>
                      <BiRefresh size={24} />
                    </Button>
                  </OverlayTrigger>
                </div>

                <OrderStatus order={order} />
              </div>
            )}
          </Col>

          <Col md={12} lg={5} sm={12} xl={4}>
            <div className="confirmation-sidebar-inline">
              {order && <OrderLoyalty order={order} />}

              <div className="order-summary-card">
                <div className="col-md-12 border-top-primary mb-3"></div>
                <h3 className="summary-section-title">
                  Your Order ({productQty})
                </h3>
                <ul className="order-items-list">
                  {order?.products.map(
                    (product: OrderProductModel, key: any) => (
                      <li key={key} className="order-item">
                        <div className="item-details">
                          <h4>{product.product_name}</h4>
                          {product?.modifier_groups?.map(
                            (modifierGroup: OrderModifierGroupModel) => (
                              <div
                                key={modifierGroup.g_id}
                                className="item-modifiers"
                              >
                                <strong>{modifierGroup.g_name}:</strong>
                                {modifierGroup.modifiers.map(
                                  (modifier, index) => (
                                    <span key={index}>
                                      {" "}
                                      {modifier.m_name}
                                      {Number(modifier.m_qty || 1) > 1 && (
                                        <span className="small">
                                          {" "}
                                          x {modifier.m_qty}
                                        </span>
                                      )}{" "}
                                      {index <
                                      modifierGroup.modifiers.length - 1
                                        ? ","
                                        : ""}
                                    </span>
                                  ),
                                )}
                              </div>
                            ),
                          )}
                          {product.product_instruction && (
                            <div className="item-modifiers text-italic">
                              {product.product_instruction}
                            </div>
                          )}
                        </div>
                        <div className="item-price-qty">
                          <span className="item-qty">
                            x{product.product_qty}
                          </span>
                          <span>
                            {Number(product.product_discount) > 0 && (
                              <span className="text-danger cut-price text-decoration-line-through me-1">
                                {formatCurrency(Number(product.product_price))}
                              </span>
                            )}
                            <span>
                              {formatCurrency(
                                Number(product.product_price) -
                                  Number(product.product_discount),
                              )}
                            </span>
                          </span>
                        </div>
                      </li>
                    ),
                  )}
                </ul>

                {order?.order_instructions && (
                  <div className="mb-4">
                    <h3 className="summary-section-title">
                      Order Instructions
                    </h3>
                    <p className="text-muted small">
                      {order.order_instructions}
                    </p>
                  </div>
                )}

                <h3 className="summary-section-title">Payment Summary</h3>
                <div className="payment-summary-table choose_payment_subtotal common-card-border">
                  <table>
                    <tbody>
                      {order?.total
                        .filter((item: any) => Number(item.price) !== 0)
                        .map((item: any) => {
                          const isDiscount = ["discount", "loyalty"].includes(
                            item.id,
                          );
                          const isTotal = item.id === "ordertotal";

                          return (
                            <tr
                              key={item.id}
                              className={`${isDiscount ? "discount" : ""} ${isTotal ? "total" : ""}`}
                            >
                              <td
                                className={`${isDiscount ? "text-success" : ""}`}
                              >
                                <span className="d-flex align-items-center gap-1">
                                  {item.name}{" "}
                                  {item.id === "shipping" &&
                                    order.is_third_party_delivery == "1" && (
                                      <DoorDashIcon size={18} />
                                    )}
                                </span>
                              </td>
                              <td
                                className={`${isDiscount ? "text-success" : ""}`}
                              >
                                {formatCurrency(Number(item.price))}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                {order?.payment_mode && (
                  <div className="payment-mode-footer">
                    <label>Payment Mode</label>
                    <span>{order.payment_mode.toUpperCase()}</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>
      {order && (
        <OrderDetailModal
          order={order}
          show={activeModal === "orderDetail"}
          handleClose={handleClose}
          handleFavOrder={handleFavOrder}
        />
      )}
    </Layout>
  );
};

export default OrderConfirmationPage;
